/* eslint-disable max-lines-per-function */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { Loader2, AlertCircle, Mail, KeyRound } from 'lucide-react';
import logoEmblem from '@/assets/logos/vehix-emblem.png';

export const ResetPasswordConfirmPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setOtpCode(value);
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const digits = pasted.replace(/[^0-9]/g, '').slice(0, 6);
    setOtpCode(digits);
  };

  /* eslint-disable max-lines-per-function */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim().toLowerCase(),
        token: otpCode,
        type: 'recovery'
      });

      if (verifyError) {
        if (
          verifyError.message.includes('expired') ||
          verifyError.message.includes('Token has expired')
        ) {
          setError('This code has expired. Please request a new password reset email.');
        } else if (
          verifyError.message.includes('invalid') ||
          verifyError.message.includes('Invalid')
        ) {
          setError('Invalid code. Please check and try again.');
        } else {
          setError(verifyError.message || 'Verification failed. Please try again.');
        }
        return;
      }

      if (data?.session) {
        navigate('/auth/reset-password', { replace: true });
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (err) {
      setError('Unable to verify code. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use card surface so input text is readable in light and dark
  const inputBase =
    'w-full px-3 py-2 border border-input rounded-md ' +
    'bg-card text-card-foreground placeholder:text-muted-foreground ' +
    'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ' +
    'disabled:opacity-50 disabled:cursor-not-allowed ' +
    'dark:bg-card dark:text-card-foreground dark:placeholder:text-muted-foreground ' +
    'dark:border-input';

  return (
    <div className='min-h-screen bg-background flex items-center justify-center p-4'>
      <div className='w-full max-w-md bg-card rounded-lg shadow-lg p-8 border border-border'>
        <div className='text-center mb-6'>
          <div className='flex justify-center mb-4'>
            <img src={logoEmblem} alt='Vehix Logo' className='w-56 h-56' />
          </div>
          <div className='flex items-center justify-center mb-2'>
            <KeyRound className='h-6 w-6 text-primary mr-2' />
            <h1 className='text-2xl font-bold text-foreground'>Reset Your Password</h1>
          </div>
          <p className='text-muted-foreground'>
            Enter your email and the 6-digit code from your password reset email.
          </p>
        </div>

        {error && (
          <div
            className={
              'mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 ' +
              'dark:border-red-800 rounded-md flex items-center gap-2'
            }
          >
            <AlertCircle className='h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0' />
            <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label htmlFor='reset-email' className='block text-sm font-medium text-foreground mb-2'>
              Email Address
            </label>
            <div className='relative'>
              <input
                id='reset-email'
                type='email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder='Enter your email'
                className={`${inputBase} pl-10`}
                required
                disabled={isSubmitting}
                autoComplete='email'
              />
              <Mail className='absolute left-3 top-2.5 h-5 w-5 text-muted-foreground' />
            </div>
          </div>

          <div>
            <label
              htmlFor='reset-otp-code'
              className='block text-sm font-medium text-foreground mb-2'
            >
              One-Time Code
            </label>
            <input
              id='reset-otp-code'
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              maxLength={6}
              value={otpCode}
              onChange={handleOtpChange}
              onPaste={handleOtpPaste}
              placeholder='000000'
              className={`${inputBase} py-3 text-center text-2xl tracking-[0.3em] font-mono`}
              required
              disabled={isSubmitting}
              autoComplete='one-time-code'
            />
            <p className='mt-1 text-xs text-muted-foreground text-center'>
              Enter the 6-digit code from your password reset email
            </p>
          </div>

          <button
            type='submit'
            disabled={isSubmitting || !email || otpCode.length !== 6}
            className={
              'w-full bg-primary text-primary-foreground py-2 px-4 rounded-md ' +
              'hover:opacity-90 transition-colors font-medium disabled:opacity-50 ' +
              'disabled:cursor-not-allowed flex items-center justify-center gap-2'
            }
          >
            {isSubmitting ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin' />
                Verifying...
              </>
            ) : (
              'Verify & Continue'
            )}
          </button>
        </form>

        <div className='mt-6 pt-4 border-t border-border'>
          <p className='text-sm text-muted-foreground text-center mb-3'>
            Didn&apos;t receive a code or it expired?
          </p>
          <p className='text-xs text-muted-foreground text-center'>
            Go back to the sign-in page and request a new password reset. Codes expire for your
            security.
          </p>
        </div>

        <div className='mt-4 text-center'>
          <a href='/auth' className='text-sm text-primary hover:opacity-90 font-semibold'>
            Back to Sign In
          </a>
        </div>
      </div>
    </div>
  );
};
